// Pure, framework-free logic for training classes (see
// `features/training-classes.md`). A company runs training classes; each has a
// free-text schedule and description, one or more coaches attached, athletes
// enrolled, and dated sessions whose attendance is tracked. Like `teams.ts`,
// this module holds the shared field validation used by both the client (form
// feedback) and the server (enforcement), plus a small attendance summariser.

import { isRealIsoDate } from './registration';
import type {
  AttendanceRecord,
  AttendanceStatus,
  TrainingAudience,
  TrainingSlot,
  Weekday,
} from './types';

/** Max length accepted for a training class's name. */
export const TRAINING_CLASS_NAME_MAX = 120;
/** Max length accepted for a training class's description. */
export const TRAINING_CLASS_DESCRIPTION_MAX = 2000;
/** Max number of weekly slots a class's schedule may hold. */
export const TRAINING_SLOT_MAX = 21;
/** Max length accepted for a training session's note. */
export const TRAINING_SESSION_NOTE_MAX = 500;

/** The weekdays in display / sort order (Mon…Sun). */
export const WEEKDAYS: readonly Weekday[] = [
  'MON',
  'TUE',
  'WED',
  'THU',
  'FRI',
  'SAT',
  'SUN',
] as const;

/** Whether a string is one of the known weekday codes. */
export function isWeekday(value: string): value is Weekday {
  return (WEEKDAYS as readonly string[]).includes(value);
}

/** The audiences a class can be marked with, in display order. */
export const TRAINING_AUDIENCES: readonly TrainingAudience[] = [
  'ALL',
  'KIDS',
  'ADULTS',
] as const;

/** Whether a string is one of the known training-audience codes. */
export function isTrainingAudience(value: string): value is TrainingAudience {
  return (TRAINING_AUDIENCES as readonly string[]).includes(value);
}

/**
 * Whether a class marked `audience` should appear under a viewer's audience
 * filter. A class open to everyone (`ALL`) matches either filter — a kid can
 * attend an all-ages class — while the `ALL` filter (no filtering) matches
 * every class.
 */
export function matchesAudience(
  audience: TrainingAudience,
  filter: TrainingAudience,
): boolean {
  return filter === 'ALL' || audience === 'ALL' || audience === filter;
}

// A 24-hour "HH:MM" clock time, 00:00–23:59.
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Whether a string is a valid 24-hour `"HH:MM"` time. */
export function isClockTime(value: string): boolean {
  return TIME_PATTERN.test(value);
}

/** Minutes since midnight for a valid `"HH:MM"` time (NaN if malformed). */
function minutesOfDay(time: string): number {
  if (!isClockTime(time)) return Number.NaN;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

/** A slot's length in whole minutes (NaN when either time is malformed). */
export function slotDurationMinutes(slot: Pick<TrainingSlot, 'startTime' | 'endTime'>): number {
  return minutesOfDay(slot.endTime) - minutesOfDay(slot.startTime);
}

/** The part of the day a session belongs to, by its start time. */
export type DayPeriod = 'MORNING' | 'AFTERNOON' | 'EVENING';

/** The day periods in display order (morning first). */
export const DAY_PERIODS: readonly DayPeriod[] = [
  'MORNING',
  'AFTERNOON',
  'EVENING',
] as const;

/**
 * Classify a session's start time into morning (before 12:00), afternoon
 * (12:00–16:59) or evening (17:00 onward) — the blocks the schedule view
 * groups a day's sessions under. A malformed time lands in MORNING so a bad
 * row still renders somewhere rather than vanishing.
 */
export function dayPeriodOf(startTime: string): DayPeriod {
  const minutes = minutesOfDay(startTime);
  if (Number.isNaN(minutes) || minutes < 12 * 60) return 'MORNING';
  if (minutes < 17 * 60) return 'AFTERNOON';
  return 'EVENING';
}

/**
 * Canonical order of two slots: by weekday (Mon…Sun), then start time, then
 * end time. Used to keep a stored schedule stable regardless of input order.
 */
export function compareSlots(a: TrainingSlot, b: TrainingSlot): number {
  const dayDiff = WEEKDAYS.indexOf(a.day) - WEEKDAYS.indexOf(b.day);
  if (dayDiff !== 0) return dayDiff;
  const startDiff = minutesOfDay(a.startTime) - minutesOfDay(b.startTime);
  if (startDiff !== 0) return startDiff;
  return minutesOfDay(a.endTime) - minutesOfDay(b.endTime);
}

/**
 * One line of a group's collapsed weekly pattern: the weekdays (Mon…Sun
 * order, deduplicated) on which the group meets at an identical start and
 * end time. "Tue & Thu 16:00–17:30" is one pattern with two days.
 */
export interface SchedulePattern {
  days: Weekday[];
  startTime: string;
  endTime: string;
}

/**
 * Collapse a schedule into per-time patterns, so a group's weekly rhythm
 * reads as one line per meeting time ("Tue, Thu · 16:00–17:30") instead of
 * one chip per slot. The input need not be canonically ordered (the class
 * dialog previews raw editor state); a copy is sorted with `compareSlots`
 * first, so days within a pattern come out Mon…Sun and patterns follow their
 * first slot's canonical position. Exact duplicates collapse to one day;
 * the same day at two different times lands in two patterns; times are
 * grouped by string identity, so malformed values never throw.
 */
export function groupSchedulePatterns(
  schedule: readonly TrainingSlot[],
): SchedulePattern[] {
  const sorted = [...schedule].sort(compareSlots);
  const byTime = new Map<string, SchedulePattern>();
  for (const slot of sorted) {
    const key = `${slot.startTime} ${slot.endTime}`;
    const pattern = byTime.get(key);
    if (!pattern) {
      byTime.set(key, {
        days: [slot.day],
        startTime: slot.startTime,
        endTime: slot.endTime,
      });
    } else if (!pattern.days.includes(slot.day)) {
      pattern.days.push(slot.day);
    }
  }
  return [...byTime.values()];
}

/**
 * Render one pattern as "Tue, Thu · 16:00–17:30". Takes the day-label lookup
 * as a callback so this stays framework-free while the UI supplies its
 * translated weekday names; days are joined with ", " (no conjunction word,
 * so no extra i18n).
 */
export function schedulePatternLabel(
  pattern: SchedulePattern,
  dayLabel: (day: Weekday) => string,
): string {
  return `${pattern.days.map(dayLabel).join(', ')} · ${pattern.startTime}–${pattern.endTime}`;
}

/** Whether a string is one of the known attendance statuses. */
export function isAttendanceStatus(value: string): value is AttendanceStatus {
  return value === 'PRESENT' || value === 'ABSENT';
}

/** Per-field validation messages; an absent key means that field is valid. */
export interface TrainingClassErrors {
  name?: string;
  description?: string;
  schedule?: string;
  audience?: string;
}

export interface TrainingClassValidation {
  valid: boolean;
  errors: TrainingClassErrors;
  /** Normalized fields — present only when `valid` is true. */
  value: {
    name: string;
    description: string;
    schedule: TrainingSlot[];
    audience: TrainingAudience;
  } | null;
}

/**
 * Validate and normalize a single raw schedule slot. Returns the trimmed slot
 * when it is a valid (weekday, start < end) triple, or null otherwise — so
 * `validateTrainingClass` can flag the whole schedule.
 */
function normalizeSlot(raw: unknown): TrainingSlot | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const record = raw as Record<string, unknown>;
  const day = String(record.day ?? '').trim();
  const startTime = String(record.startTime ?? '').trim();
  const endTime = String(record.endTime ?? '').trim();
  if (!isWeekday(day)) return null;
  if (!isClockTime(startTime) || !isClockTime(endTime)) return null;
  if (minutesOfDay(endTime) <= minutesOfDay(startTime)) return null;
  return { day, startTime, endTime };
}

/**
 * Validate and normalize the raw fields of a training-class form. The name is
 * required; the description is optional free text ('' = unset); the schedule
 * is an optional list of weekly slots (each a weekday plus a `HH:MM` start/end
 * with end after start), returned canonically ordered (Mon…Sun, then time);
 * the audience is an optional `TrainingAudience` code ('' / absent = 'ALL').
 */
export function validateTrainingClass(input: {
  name: string;
  description?: string;
  schedule?: unknown;
  audience?: string;
}): TrainingClassValidation {
  const errors: TrainingClassErrors = {};

  const name = input.name.trim();
  if (name.length === 0) {
    errors.name = 'Group name is required.';
  } else if (name.length > TRAINING_CLASS_NAME_MAX) {
    errors.name = `Group name must be ${TRAINING_CLASS_NAME_MAX} characters or fewer.`;
  }

  const description = (input.description ?? '').trim();
  if (description.length > TRAINING_CLASS_DESCRIPTION_MAX) {
    errors.description = `Description must be ${TRAINING_CLASS_DESCRIPTION_MAX} characters or fewer.`;
  }

  let schedule: TrainingSlot[] = [];
  const rawSchedule = input.schedule ?? [];
  if (!Array.isArray(rawSchedule)) {
    errors.schedule = 'The schedule is invalid.';
  } else if (rawSchedule.length > TRAINING_SLOT_MAX) {
    errors.schedule = `A group may have at most ${TRAINING_SLOT_MAX} weekly slots.`;
  } else {
    const normalized: TrainingSlot[] = [];
    for (const raw of rawSchedule) {
      const slot = normalizeSlot(raw);
      if (!slot) {
        errors.schedule = 'Each slot needs a weekday and an end time after its start time.';
        break;
      }
      normalized.push(slot);
    }
    if (!errors.schedule) schedule = normalized.sort(compareSlots);
  }

  const rawAudience = (input.audience ?? '').trim();
  let audience: TrainingAudience = 'ALL';
  if (rawAudience.length > 0 && !isTrainingAudience(rawAudience)) {
    errors.audience = 'Pick who the group is for.';
  } else if (rawAudience.length > 0) {
    audience = rawAudience as TrainingAudience;
  }

  const valid = Object.keys(errors).length === 0;
  return {
    valid,
    errors,
    value: valid ? { name, description, schedule, audience } : null,
  };
}

/** Per-field validation messages; an absent key means that field is valid. */
export interface TrainingSessionErrors {
  date?: string;
  note?: string;
}

export interface TrainingSessionValidation {
  valid: boolean;
  errors: TrainingSessionErrors;
  /** Normalized, trimmed fields — present only when `valid` is true. */
  value: { date: string; note: string } | null;
}

/**
 * Validate and normalize a training-session form. The date is required and
 * must be a real ISO `YYYY-MM-DD` calendar date; the note is optional free
 * text ('' = unset), length-bounded.
 */
export function validateTrainingSession(input: {
  date: string;
  note?: string;
}): TrainingSessionValidation {
  const errors: TrainingSessionErrors = {};

  const date = input.date.trim();
  if (date.length === 0) {
    errors.date = 'A session date is required.';
  } else if (!isRealIsoDate(date)) {
    errors.date = 'Enter a valid date.';
  }

  const note = (input.note ?? '').trim();
  if (note.length > TRAINING_SESSION_NOTE_MAX) {
    errors.note = `Note must be ${TRAINING_SESSION_NOTE_MAX} characters or fewer.`;
  }

  const valid = Object.keys(errors).length === 0;
  return { valid, errors, value: valid ? { date, note } : null };
}

/** An athlete's attendance totals across a set of sessions. */
export interface AttendanceSummary {
  present: number; // sessions marked present
  absent: number; // sessions marked absent
  marked: number; // sessions with any mark (present + absent)
  rate: number; // present / marked, in [0, 1]; 0 when nothing marked
}

/**
 * Summarise one athlete's attendance from their marks. Pure and order-free, so
 * both the client (per-athlete stats) and tests can reuse it. `rate` is the
 * share of *marked* sessions the athlete attended (sessions with no mark do
 * not count against them).
 */
export function summarizeAttendance(
  records: readonly Pick<AttendanceRecord, 'status'>[],
): AttendanceSummary {
  let present = 0;
  let absent = 0;
  for (const record of records) {
    if (record.status === 'PRESENT') present += 1;
    else if (record.status === 'ABSENT') absent += 1;
  }
  const marked = present + absent;
  return { present, absent, marked, rate: marked === 0 ? 0 : present / marked };
}
